import { z } from 'zod'
import type { CalculatorDefinition } from '@/types/calculator'
import { positiveMoney } from '@/calculators/shared/schema'
import { calculateCarAffordability } from './calculation'
import { content, faqs } from './content'
import type { CarAffordabilityInputs, CarAffordabilityResult } from './types'

const schema = z.object({
  monthlyIncome: positiveMoney(),
  deposit: positiveMoney(),
  vehiclePrice: positiveMoney('Enter a vehicle price').refine((v) => v > 0, {
    message: 'Vehicle price must be greater than 0',
  }),
  interestRate: z.number().min(0).max(1),
  termYears: z.number().int().min(1).max(10),
  balloonPercent: z.number().min(0).max(0.6),
  insuranceMonthly: positiveMoney(),
  fuelMonthly: positiveMoney(),
  maintenanceMonthly: positiveMoney(),
})

const defaults: CarAffordabilityInputs = {
  monthlyIncome: 35_000,
  deposit: 50_000,
  vehiclePrice: 300_000,
  interestRate: 0.13,
  termYears: 6,
  balloonPercent: 0,
  insuranceMonthly: 1_500,
  fuelMonthly: 3_000,
  maintenanceMonthly: 1_000,
}

export const carAffordabilityDefinition: CalculatorDefinition<
  CarAffordabilityInputs,
  CarAffordabilityResult
> = {
  id: 'car-affordability',
  slug: 'car-affordability-calculator',
  category: 'vehicle',
  title: 'Car Affordability Calculator',
  shortTitle: 'Car Affordability',
  description:
    'Estimate vehicle finance instalments, total cost and monthly ownership affordability in South Africa.',
  inputs: [
    {
      name: 'monthlyIncome',
      label: 'Monthly gross income',
      type: 'money',
      required: false,
      unit: 'R',
      placeholder: 'e.g. 35 000',
    },
    {
      name: 'vehiclePrice',
      label: 'Vehicle price',
      type: 'money',
      required: true,
      unit: 'R',
      placeholder: 'e.g. 300 000',
    },
    {
      name: 'deposit',
      label: 'Deposit / trade-in',
      type: 'money',
      required: false,
      unit: 'R',
      placeholder: '0',
    },
    {
      name: 'interestRate',
      label: 'Interest rate',
      type: 'percentage',
      required: true,
      unit: '%',
      min: 0,
      max: 1,
      step: 0.001,
      defaultValue: 0.13,
    },
    {
      name: 'termYears',
      label: 'Finance term',
      type: 'number',
      required: true,
      unit: 'years',
      min: 1,
      max: 10,
      step: 1,
      defaultValue: 6,
    },
    {
      name: 'balloonPercent',
      label: 'Balloon / residual',
      type: 'percentage',
      required: true,
      unit: '%',
      min: 0,
      max: 0.6,
      step: 0.01,
      defaultValue: 0,
      hint: 'Lump sum due at the end of the term as a percentage of finance amount',
    },
    {
      name: 'insuranceMonthly',
      label: 'Insurance (monthly)',
      type: 'money',
      required: false,
      unit: 'R',
      placeholder: '0',
    },
    {
      name: 'fuelMonthly',
      label: 'Fuel / charging (monthly)',
      type: 'money',
      required: false,
      unit: 'R',
      placeholder: '0',
    },
    {
      name: 'maintenanceMonthly',
      label: 'Maintenance (monthly)',
      type: 'money',
      required: false,
      unit: 'R',
      placeholder: '0',
    },
  ],
  defaults,
  schema,
  calculate: calculateCarAffordability,
  resultSections: [
    {
      id: 'monthlyInstalment',
      label: 'Monthly instalment',
      format: 'currency',
      value: (r: CarAffordabilityResult) => r.monthlyInstalment,
      highlighted: true,
    },
    {
      id: 'totalMonthlyOwnershipCost',
      label: 'Total monthly ownership cost',
      format: 'currency',
      value: (r: CarAffordabilityResult) => r.totalMonthlyOwnershipCost,
      highlighted: true,
    },
    {
      id: 'affordable',
      label: 'Affordability signal',
      format: 'plain',
      value: (r: CarAffordabilityResult) => r.affordable.toUpperCase(),
      highlighted: true,
    },
    {
      id: 'financeAmount',
      label: 'Finance amount',
      format: 'currency',
      value: (r: CarAffordabilityResult) => r.financeAmount,
    },
    {
      id: 'balloonAmount',
      label: 'Balloon / residual amount',
      format: 'currency',
      value: (r: CarAffordabilityResult) => r.balloonAmount,
    },
    {
      id: 'totalVehicleCost',
      label: 'Total vehicle cost',
      format: 'currency',
      value: (r: CarAffordabilityResult) => r.totalVehicleCost,
    },
    {
      id: 'totalFinanceCost',
      label: 'Total finance cost',
      format: 'currency',
      value: (r: CarAffordabilityResult) => r.totalFinanceCost,
    },
    {
      id: 'affordabilityRatio',
      label: 'Ownership cost to income',
      format: 'percent',
      value: (r: CarAffordabilityResult) => r.affordabilityRatio,
    },
  ],
  assumptions: [
    {
      name: 'Standard amortising vehicle finance formula with optional balloon',
      reviewedAt: '2026-09-11',
      effectiveFrom: '2026-09-01',
      notes:
        'Assumes a fixed interest rate and equal monthly instalments. Excludes initiation, admin and licensing fees. Insurance, fuel and maintenance are user estimates.',
    },
  ],
  related: ['fuel-cost-calculator', 'bond-calculator'],
  seo: {
    title: 'Car Affordability Calculator South Africa',
    description:
      'How much car can I afford? Add up instalment, insurance, fuel and maintenance with this South African car affordability calculator.',
    keywords: ['car affordability calculator', 'vehicle finance South Africa', 'car instalment'],
  },
  content,
  faqs,
}
